import React, { useMemo, useState, useRef, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import LoaderSmall from "../LoaderSmall";

const STATUS_COLORS = {
  INVITED: "#fde68a",
  "KYC UPDATED": "#bbf7d0",
  "KYC PENDING": "#fed7aa",
  REGISTERED: "#86efac",
  "NON INVITED": "#c7d2fe",
  NONINVITED: "#c7d2fe",
  "PENDING WITH L2": "#fecaca",
  ACTIVE: "#fef08a",
  "IN REGISTRATION": "#a7f3d0",
  DEACTIVATED: "#fdba74",
  CANCELLED: "#fca5a5",
  "ON BOARDED": "#93c5fd",
  "INVITATION SENT": "#f9a8d4",
  ACKNOWLEDGED: "#6ee7b7",
  NEW: "#c4b5fd",
  APPROVED: "#4ade80",
  "IN RELEASE": "#fb923c",
  CREATED: "#93c5fd",
  STARTED: "#c4b5fd",
  "IN PROGRESS": "#fde68a",
  "ON HOLD": "#fed7aa",
  COMPLETED: "#86efac",
  CLOSED: "#86efac",
  TRANSFERRED: "#c7d2fe",
  DELETED: "#d1d5db",
  DRAFT: "#e9d5ff",
  PRESENT: "#86efac",
  ABSENT: "#fca5a5",
  LATE: "#fde68a",
  "ON LEAVE": "#c4b5fd",
  HOLIDAY: "#93c5fd",
  UNKNOWN: "#e5e7eb",
};
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const percent = ((data.count / data.total) * 100).toFixed(2);
    return (
      <div
        style={{
          background: "#fff",
          padding: "8px 12px",
          borderRadius: "6px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 0 10px rgba(0,0,0,0.15)",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: "14px" }}>
          {data.name} : {percent}%
        </span>
      </div>
    );
  }
  return null;
};

const StatusDonutChartCard = ({
  title,
  data: initialData = [],
  api,
  queryName,
  keyName,
  onClick,
}) => {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const modalRef = useRef(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!api || !queryName) {
      setData(initialData || []);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        let page = 1;
        let per_page = 200;
        let combined = [];

        while (true) {
          const payload = {
            page,
            per_page,
            searchTerm: "",
            ...(queryName ? { queryName } : {}),
          };

          const res = await api(payload);

          combined = [...combined, ...(res?.data || [])];

          if (combined.length >= (res?.total || 0)) break;

          page++;
        }

        const grouped = {};

        combined.forEach((item) => {
          const key = (item[keyName] || "Unknown").toUpperCase().trim();
          if (!grouped[key]) grouped[key] = 0;
          grouped[key]++;
        });

        const formatted = Object.keys(grouped).map((key) => ({
          name: key,
          count: grouped[key],
        }));

        setData(formatted);
      } catch (err) {
        console.error("Donut fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [api, queryName, keyName, initialData]);

  const total = useMemo(
    () => data.reduce((sum, item) => sum + Number(item.count), 0),
    [data],
  );

  const chartData = data.map((d) => ({
    ...d,
    total,
    colorKey: d.name?.toUpperCase().trim(),
  }));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowModal(false);
      }
    };
    if (showModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showModal]);

  return (
    <div
      style={{
        width: "90%",
        background: "#fff",
        borderRadius: "10px",
        padding: "14px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        position: "relative",
      }}
    >
      <div
        style={{
          fontSize: "16px",
          fontWeight: 700,
          marginBottom: "10px",
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: "6px",
        }}
      >
        {title}
      </div>

      <div style={{ width: "100%", height: 260, position: "relative" }}>
        {loading ? (
          <div
            style={{
              height: "260px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LoaderSmall isLoading={true} />
          </div>
        ) : (
          <>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="count"
                  nameKey="name"
                  innerRadius="70%"
                  outerRadius="90%"
                  paddingAngle={2}
                  onClick={(d) => onClick && onClick(d)}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={
                        STATUS_COLORS[entry.colorKey] || STATUS_COLORS.UNKNOWN
                      }
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            <div
              style={{
                position: "absolute",
                top: "55%",
                left: "50%",
                transform: "translate(-50%, -60%)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "14px", color: "#94a3b8" }}>Total</div>
              <div style={{ fontSize: "22px", fontWeight: 700 }}>{total}</div>
            </div>
          </>
        )}
      </div>

      <div
        onClick={() => setShowModal(true)}
        style={{
          position: "absolute",
          bottom: "10px",
          left: "10px",
          fontSize: "20px",
          cursor: "pointer",
          color: "#555",
          transition: "0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#3b82f6")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#555")}
      >
        <i className="bi bi-eye-fill"></i>
      </div>

      {showModal && (
        <div
          ref={modalRef}
          style={{
            position: "absolute",
            bottom: "50px",
            left: "10px",
            background: "#fff",
            padding: "12px 15px",
            borderRadius: "10px",
            border: "1px solid #ddd",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            width: "220px",
            zIndex: 1050,
          }}
        >
          <div
            onClick={() => setShowModal(false)}
            style={{
              position: "absolute",
              top: "6px",
              right: "8px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            ✖
          </div>

          <div style={{ fontWeight: 700, marginBottom: "10px" }}>
            Status Summary
          </div>

          {chartData.map((item, idx) => {
            const percent = total ? ((item.count / total) * 100).toFixed(2) : 0;
            return (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  marginBottom: "6px",
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor:
                      STATUS_COLORS[item.colorKey] || STATUS_COLORS.UNKNOWN,
                  }}
                />
                {item.name}: {item.count} ({percent}%)
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatusDonutChartCard;

const STATS_PALETTE = [
  "#86efac",
  "#93c5fd",
  "#fde68a",
  "#fca5a5",
  "#c4b5fd",
  "#fdba74",
  "#a7f3d0",
  "#fecaca",
  "#bfdbfe",
  "#d9f99d",
];

const formatCurrency = (val) => {
  if (val >= 10_00_000) return `₹${(val / 10_00_000).toFixed(2)}Cr`;
  if (val >= 1_00_000) return `₹${(val / 1_00_000).toFixed(2)}L`;
  if (val >= 1_000) return `₹${(val / 1_000).toFixed(1)}K`;
  return `₹${val}`;
};

const StatsCustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const d = payload[0].payload;
    const percent = d.total ? ((d.count / d.total) * 100).toFixed(2) : 0;
    return (
      <div
        style={{
          background: "#fff",
          padding: "8px 12px",
          borderRadius: "6px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 0 10px rgba(0,0,0,0.15)",
          fontSize: "13px",
          lineHeight: "1.6",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 2 }}>{d.name}</div>
        <div>
          Count : <b>{d.count}</b> ({percent}%)
        </div>
        {d.value > 0 && (
          <div>
            Value : <b>{formatCurrency(d.value)}</b>
          </div>
        )}
      </div>
    );
  }
  return null;
};

export const StatsDonutChartCard = ({
  title,
  chartData: rawData = [], // ✅ pass res.data.budget.chartData directly (raw array)
  total = 0, // ✅ res.data.budget.total
  sum = 0, // ✅ res.data.budget.sum
  isCurrency = false, // show ₹ formatting in modal
  centerLabel = "total", // "total" = count | "sum" = formatted value
  onClick,
}) => {
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef(null);

  /* ── Split valid vs null entries ── */
  const validEntries = useMemo(
    () => rawData.filter((d) => d.status !== null && d.status !== undefined),
    [rawData],
  );

  const nullEntries = useMemo(
    () => rawData.filter((d) => d.status === null || d.status === undefined),
    [rawData],
  );

  /* ── Build recharts data — shape: { name, count, value, total, color } ── */
  const chartData = useMemo(
    () =>
      validEntries.map((d, i) => ({
        name: d.status,
        count: d.count,
        value: d.value ?? 0,
        total, // for % in tooltip
        color: STATS_PALETTE[i % STATS_PALETTE.length],
      })),
    [validEntries, total],
  );

  const nullCount = nullEntries.reduce((s, d) => s + d.count, 0);
  const nullValue = nullEntries.reduce((s, d) => s + (d.value ?? 0), 0);
  const centerDisplay =
    centerLabel === "sum" ? (isCurrency ? formatCurrency(sum) : sum) : total;

  const centerText = centerLabel === "sum" ? "Total Value" : "Total";
  useEffect(() => {
    const handler = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target))
        setShowModal(false);
    };
    if (showModal) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showModal]);

  return (
    <div
      style={{
        width: "90%",
        background: "#fff",
        borderRadius: "10px",
        padding: "14px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        position: "relative",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          fontSize: "16px",
          fontWeight: 700,
          marginBottom: "10px",
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: "6px",
        }}
      >
        {title}
      </div>

      {/* CHART */}
      <div style={{ width: "100%", height: 260, position: "relative" }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="count"
              nameKey="name"
              innerRadius="70%"
              outerRadius="90%"
              paddingAngle={2}
              onClick={(d) => onClick && onClick(d)}
            >
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<StatsCustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* CENTER LABEL */}
        <div
          style={{
            position: "absolute",
            top: "55%",
            left: "50%",
            transform: "translate(-50%, -60%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <div style={{ fontSize: "14px", color: "#94a3b8" }}>{centerText}</div>
          <div style={{ fontSize: "22px", fontWeight: 700 }}>
            {centerDisplay}
          </div>
        </div>
      </div>

      {/* EYE ICON */}
      <div
        onClick={() => setShowModal(true)}
        style={{
          position: "absolute",
          bottom: "10px",
          left: "10px",
          fontSize: "20px",
          cursor: "pointer",
          color: "#555",
          transition: "0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#3b82f6")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#555")}
      >
        <i className="bi bi-eye-fill" />
      </div>

      {/* MODAL */}
      {showModal && (
        <div
          ref={modalRef}
          style={{
            position: "absolute",
            bottom: "50px",
            left: "10px",
            background: "#fff",
            padding: "12px 15px",
            borderRadius: "10px",
            border: "1px solid #ddd",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            width: "260px",
            zIndex: 1050,
          }}
        >
          <div
            onClick={() => setShowModal(false)}
            style={{
              position: "absolute",
              top: "6px",
              right: "8px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            ✖
          </div>

          <div style={{ fontWeight: 700, marginBottom: "10px" }}>
            Stats Summary
          </div>

          {/* Named rows */}
          {chartData.map((item, idx) => {
            const percent = total ? ((item.count / total) * 100).toFixed(2) : 0;
            return (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  marginBottom: "6px",
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: item.color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1 }}>{item.name}</span>
                <span style={{ color: "#374151" }}>
                  {item.count} ({percent}%)
                </span>
                {isCurrency && item.value > 0 && (
                  <span
                    style={{ color: "#64748b", marginLeft: 4, fontSize: 12 }}
                  >
                    {formatCurrency(item.value)}
                  </span>
                )}
              </div>
            );
          })}

          {/* Unknown / null row */}
          {nullCount > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
                marginBottom: "6px",
                fontWeight: 600,
                opacity: 0.5,
              }}
            >
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: "#e5e7eb",
                  flexShrink: 0,
                }}
              />
              <span style={{ flex: 1 }}>Unknown</span>
              <span>
                {nullCount} (
                {total ? ((nullCount / total) * 100).toFixed(2) : 0}%)
              </span>
              {isCurrency && nullValue > 0 && (
                <span style={{ color: "#64748b", marginLeft: 4, fontSize: 12 }}>
                  {formatCurrency(nullValue)}
                </span>
              )}
            </div>
          )}

          {/* Footer */}
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              marginTop: "8px",
              paddingTop: "8px",
              fontSize: "13px",
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 700,
            }}
          >
            <span>Total: {total}</span>
            {isCurrency && <span>{formatCurrency(sum)}</span>}
          </div>
        </div>
      )}
    </div>
  );
};
